import classnames from "classnames";
import { entries, values } from "lodash-es";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ProductTileModel } from "@api/mappers/products.mapper";

import {
    BreakpointProvider,
    useBreakpoint,
} from "@utils/breakpoints/breakpoints.provider";
import { AlgoliaRecommendZones, ProductSliderSources } from "@utils/enums";
import { attemptMethod } from "@utils/general";
import { ProductCartContext } from "@utils/hooks/cart.hook";
import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { parseElementForCommentedContent } from "@utils/ssr";
import { mapSsrLayoutClasses } from "@utils/swiper";

import { ParamsAlgolia } from "@services/algolia/types/paramsAlgolia.types";
import { BreakpointMinSizes } from "@services/breakpoint.service";

import { useThunkDispatch } from "@redux/dispatcher";
import { load as loadCart } from "@redux/modules/api/data/cart/cart.module";
import { getCart } from "@redux/modules/api/data/cart/cart.selectors";

import { RecentsProductslider } from "@components/Product/ProductSlider/recents/recents.component";

import { ProductTileApiModel } from "../../../@types/service/product/api/productTile.api.model";
import { AlgoliaProductSlider } from "./algolia/algolia.component";
import { AlgoliaRecommendProductslider } from "./alogliaRecommend/algoliaRecommend.component";
import { SKUProductSlider } from "./sku/sku.component";

export type ProductSliderContainerProps = {
    showButton: boolean;
    showCategoryLink: boolean;
    allowInstantAddToCart: boolean;
    amountOfFeatures: number;
    useInsidePadding: boolean;
    productSource: ProductSliderSources;
    productSku?: string;
    url?: string;
    title: string;
    breakpointsLayoutSettings: string | any;
    prefetchedData?: Array<ProductTileModel>;
    prefetch?: boolean;
    prefetchDataIdentifier?: string;
    handPickedSkuList?: string[];
    lazyLoad?: boolean;
    paramsAlgolia?: ParamsAlgolia;
    hiddenBreakpoints?: Array<BreakpointMinSizes>;
    className?: string;
};

const ProductSliderContainer = ({
    hiddenBreakpoints = [],
    className,
    ...rest
}: ProductSliderContainerProps) => {
    const classes = values(BreakpointMinSizes).map(breakpoint =>
        hiddenBreakpoints.includes(breakpoint)
            ? `d-${breakpoint}-none`
            : `d-${breakpoint}-block`,
    );

    return (
        <div className={classnames(className, ...classes)}>
            <BreakpointProvider>
                <RenderProductSliderOnBreakpointWrapper
                    {...rest}
                    hiddenBreakpoints={hiddenBreakpoints}
                />
            </BreakpointProvider>
        </div>
    );
};

const RenderProductSliderOnBreakpointWrapper = ({
    hiddenBreakpoints = [],
    ...rest
}: ProductSliderContainerProps) => {
    const { breakpoints } = useBreakpoint();
    const isClientSide = useIsClientSide();

    const matchingBreakpoint =
        entries(breakpoints)
            .filter(([, matches]) => matches)
            .map(([breakpoint]) => breakpoint)
            .at(-1) ?? "xxs";

    const renderServer = !isClientSide;
    const renderClient =
        isClientSide &&
        Object.keys(breakpoints).length > 0 &&
        !hiddenBreakpoints.includes(matchingBreakpoint as BreakpointMinSizes);

    if (!renderServer && !renderClient) {
        return null;
    }

    return <_ProductSliderContainer {...rest} isClientSide={isClientSide} />;
};

const _ProductSliderContainer = ({
    showButton,
    showCategoryLink,
    allowInstantAddToCart,
    amountOfFeatures,
    useInsidePadding,
    productSource,
    url,
    title,
    breakpointsLayoutSettings,
    paramsAlgolia,
    prefetchedData,
    prefetchDataIdentifier,
    prefetch,
    productSku,
    handPickedSkuList,
    lazyLoad = true,
    isClientSide,
}: Omit<ProductSliderContainerProps, "className" | "hiddenBreakpoints"> & {
    isClientSide: boolean;
}) => {
    const dispatch = useThunkDispatch();
    const [data, setData] = useState(prefetchedData);
    const cart = useSelector(getCart);

    const layoutSettingsJson: {
        [key: string]: any;
    } = attemptMethod(() => JSON.parse(breakpointsLayoutSettings), {});
    const ssrLayoutClasses = mapSsrLayoutClasses(layoutSettingsJson);

    useEffect(() => {
        if (allowInstantAddToCart) {
            dispatch(loadCart());
        }
    }, []);

    useEffect(() => {
        if (
            prefetch ||
            (url === AlgoliaRecommendZones.addToCart && !prefetchDataIdentifier)
        ) {
            return;
        }
        const newDataElement = document.querySelector<HTMLElement>(
            `script[data-ssr-data-uuid='${prefetchDataIdentifier}']`,
        );
        const newData = parseElementForCommentedContent(newDataElement);
        if (newData) {
            setData(newData.prefetchedData);
        }
    }, [isClientSide]);

    return (
        <ProductCartContext
            value={{
                allowInstantAddToCart: allowInstantAddToCart,
                cart: cart,
            }}
        >
            {productSource === ProductSliderSources.handPicked && (
                <SKUProductSlider
                    title={title}
                    showButton={showButton}
                    showCategoryLink={showCategoryLink}
                    useInsidePadding={useInsidePadding}
                    skuList={handPickedSkuList || []}
                    breakpointslayoutsettings={breakpointsLayoutSettings}
                    ssrLayoutClasses={ssrLayoutClasses}
                    amountOfFeatures={amountOfFeatures}
                    prefetchedData={data}
                    lazyLoad={lazyLoad}
                />
            )}
            {productSource === ProductSliderSources.accessory && (
                <SKUProductSlider
                    title={title}
                    showButton={showButton}
                    showCategoryLink={showCategoryLink}
                    useInsidePadding={useInsidePadding}
                    skuList={[]}
                    breakpointslayoutsettings={breakpointsLayoutSettings}
                    ssrLayoutClasses={ssrLayoutClasses}
                    amountOfFeatures={amountOfFeatures}
                    prefetchedData={data}
                    lazyLoad={lazyLoad}
                />
            )}
            {productSource === ProductSliderSources.mainProduct && (
                <SKUProductSlider
                    title={title}
                    showButton={showButton}
                    showCategoryLink={showCategoryLink}
                    useInsidePadding={useInsidePadding}
                    skuList={[]}
                    breakpointslayoutsettings={breakpointsLayoutSettings}
                    ssrLayoutClasses={ssrLayoutClasses}
                    amountOfFeatures={amountOfFeatures}
                    prefetchedData={data}
                    lazyLoad={lazyLoad}
                />
            )}
            {productSource === ProductSliderSources.recent && (
                <RecentsProductslider
                    title={title}
                    showButton={showButton}
                    showCategoryLink={showCategoryLink}
                    useInsidePadding={useInsidePadding}
                    breakpointslayoutsettings={breakpointsLayoutSettings}
                    ssrLayoutClasses={ssrLayoutClasses}
                    amountOfFeatures={amountOfFeatures}
                    lazyLoad={lazyLoad}
                    productSku={productSku}
                />
            )}
            {productSource === ProductSliderSources.algoliaRecommend &&
                paramsAlgolia &&
                url && (
                    <AlgoliaRecommendProductslider
                        title={title}
                        showButton={showButton}
                        showCategoryLink={showCategoryLink}
                        useInsidePadding={useInsidePadding}
                        zoneId={url}
                        breakpointslayoutsettings={breakpointsLayoutSettings}
                        ssrLayoutClasses={ssrLayoutClasses}
                        amountOfFeatures={amountOfFeatures}
                        prefetchedData={data}
                        productSku={productSku}
                        lazyLoad={lazyLoad}
                        paramsAlgolia={paramsAlgolia}
                    />
                )}
            {productSource === ProductSliderSources.algolia &&
                paramsAlgolia && (
                    <AlgoliaProductSlider
                        title={title}
                        showButton={showButton}
                        showCategoryLink={showCategoryLink}
                        useInsidePadding={useInsidePadding}
                        paramsAlgolia={paramsAlgolia}
                        breakpointslayoutsettings={breakpointsLayoutSettings}
                        ssrLayoutClasses={ssrLayoutClasses}
                        amountOfFeatures={amountOfFeatures}
                        lazyLoad={lazyLoad}
                    />
                )}
        </ProductCartContext>
    );
};

export { ProductSliderContainer };
